import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import axios from "axios";
import { API_BASE } from "../services/api";

// --- Types ---
interface Account {
  id: number;
  user_id: string;
  hub_auth_key: string;
  user_tier?: number | null;
  org_id?: string; // ✅ 조직 ID 추가
  created_at?: string;
}

// ✅ 조직 정보 인터페이스 추가
interface Organization {
  id: string;
  name: string;
  description?: string;
}

interface Props {
  setErrorMsg: (msg: string | null) => void;
}

function UserAccessManager({ setErrorMsg }: Props) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]); // ✅ 조직 목록 State
  
  const [openDialog, setOpenDialog] = useState(false);
  
  // 입력 Form State
  const [newUserId, setNewUserId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUserTier, setNewUserTier] = useState<number>(2); // 기본값: 2 (Builder)
  const [newOrgId, setNewOrgId] = useState<string>("KR"); // ✅ 기본값: KR
  
  const [isCreating, setIsCreating] = useState(false);

  // 로딩/진행 상태
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const [regenBusyId, setRegenBusyId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const authHeaders = () => ({
    "x-user-id": localStorage.getItem("user_id") || "",
    "x-access-token": localStorage.getItem("access_token") || "",
    "x-refresh-token": localStorage.getItem("refresh_token") || "",
  });

  // 1. 사용자 목록 조회
  const fetchAccounts = () => {
    setLoadingAccounts(true);
    axios
      .get(`${API_BASE}admin/users`, { headers: authHeaders() })
      .then((res) => {
        const body = res.data;
        if (body.success) {
          // 오너( tier===0 ) 및 'admin' 계정은 목록에서 제외 (정책에 따라 조정 가능)
          const raw: Account[] = body.data || [];
          const filtered = raw.filter(
            (acc) =>
              (acc.user_tier ?? -1) !== 0 &&
              (acc.user_id || "").toLowerCase() !== "admin"
          );
          setAccounts(filtered);
          setErrorMsg(null);
        } else {
          setErrorMsg(body.message || body.error || "계정 목록 조회 실패");
        }
      })
      .catch((err) => {
        console.error(err);
        setErrorMsg("계정 목록을 불러오는 중 오류가 발생했습니다.");
      })
      .finally(() => setLoadingAccounts(false));
  };

  // 2. 조직 목록 조회 (✅ 추가)
  const fetchOrganizations = () => {
    axios
      .get(`${API_BASE}organizations`, { headers: authHeaders() })
      .then((res) => {
        if (res.data.success) {
          setOrgs(res.data.data || []);
        }
      })
      .catch((err) => {
        console.warn("조직 목록 로드 실패:", err);
        // 실패해도 치명적이지 않으므로 경고만 로그
      });
  };

  useEffect(() => {
    fetchAccounts();
    fetchOrganizations(); // ✅ 초기 로딩 시 조직 목록도 호출
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    const uid = newUserId.trim();
    const pw = newPassword;
    if (!uid) return;
    if (!pw || pw.length < 8) {
      setErrorMsg("비밀번호는 8자리 이상이어야 합니다.");
      return;
    }

    setIsCreating(true);
    try {
      const res = await axios.post(
        `${API_BASE}admin/users`,
        { 
          user_id: uid, 
          password: pw, 
          user_tier: newUserTier,
          org_id: newOrgId // ✅ 조직 정보 함께 전송
        }, 
        { headers: authHeaders() }
      );
      const body = res.data;
      if (body.success) {
        setOpenDialog(false);
        // 입력 필드 초기화
        setNewUserId("");
        setNewPassword("");
        setNewUserTier(2); 
        setNewOrgId("KR"); // 리셋
        fetchAccounts();
      } else {
        setErrorMsg(body.message || body.error || "계정 생성 실패");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("계정 생성 중 오류가 발생했습니다.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const res = await axios.delete(`${API_BASE}admin/users/${id}`, {
        headers: authHeaders(),
      });
      const body = res.data;
      if (body.success) {
        fetchAccounts();
      } else {
        setErrorMsg(body.message || body.error || "계정 삭제 실패");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("계정 삭제 중 오류가 발생했습니다.");
    } finally {
      setDeletingId(null);
    }
  };

  // Hub Auth Key 재발급
  const handleRegenerateHubKey = async (id: string) => {
    setRegenBusyId(id);
    try {
      const res = await axios.put(
        `${API_BASE}users/${id}/regenerate`,
        {},
        { headers: authHeaders() }
      );
      const body = res.data;
      if (body.success) {
        fetchAccounts(); 
      } else {
        setErrorMsg(body.message || body.error || "Hub Auth Key 재발급 실패");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Hub Auth Key 재발급 중 오류가 발생했습니다.");
    } finally {
      setRegenBusyId(null);
    }
  };

  const passwordError =
    newPassword.length > 0 && newPassword.length < 8
      ? "비밀번호는 8자리 이상이어야 합니다."
      : "";

  // Tier 표시용 헬퍼 함수
  const renderTierChip = (tier?: number | null) => {
    if (tier === 1) {
      return <Chip label="Architect (1)" size="small" color="primary" variant="outlined" />;
    }
    if (tier === 2) {
      return <Chip label="Builder (2)" size="small" color="default" variant="outlined" />;
    }
    return <Chip label="Unknown" size="small" />;
  };

  return (
    <Box>
      {/* 상단 로딩 배너 */}
      {loadingAccounts && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>계정 목록을 불러오는 중...</Typography>
          </Box>
          <LinearProgress />
        </Paper>
      )}

      {/* 계정 목록 헤더 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">계정 목록</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          disabled={loadingAccounts}
        >
          계정 추가
        </Button>
      </Box>

      {/* 테이블 */}
      <TableContainer component={Paper} sx={{ bgcolor: "#1e1e1e" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#ccc" }}>ID</TableCell>
              <TableCell sx={{ color: "#ccc" }}>User ID</TableCell>
              {/* [추가됨] 소속 컬럼 */}
              <TableCell sx={{ color: "#ccc" }}>소속 (Org)</TableCell>
              {/* [추가됨] 권한 컬럼 */}
              <TableCell sx={{ color: "#ccc" }}>권한 (Tier)</TableCell>
              <TableCell sx={{ color: "#ccc" }}>생성일</TableCell>
              <TableCell sx={{ color: "#ccc" }}>Hub Auth Key</TableCell>
              <TableCell sx={{ color: "#ccc" }} align="center">
                재발급
              </TableCell>
              <TableCell sx={{ color: "#ccc" }} align="right">
                삭제
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingAccounts && accounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ color: "#888", textAlign: "center", py: 4 }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <CircularProgress size={18} />
                    <span>불러오는 중...</span>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {!loadingAccounts &&
              accounts.map((acc) => (
                <TableRow key={acc.id} hover>
                  <TableCell sx={{ color: "#fff" }}>{acc.id}</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>{acc.user_id}</TableCell>
                  
                  {/* [추가됨] 소속(Org) 표시 */}
                  <TableCell>
                    <Chip 
                      label={acc.org_id || "KR"} 
                      size="small" 
                      variant="outlined" 
                      sx={{ color: "#ccc", borderColor: "#555" }} 
                    />
                  </TableCell>

                  {/* Tier 표시 */}
                  <TableCell>
                    {renderTierChip(acc.user_tier)}
                  </TableCell>
                  
                  <TableCell sx={{ color: "#ccc" }}>{acc.created_at || "-"}</TableCell>
                  <TableCell sx={{ color: "#fff", fontFamily: "monospace" }}>
                    {acc.hub_auth_key}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AutorenewIcon />}
                      disabled={regenBusyId === acc.user_id || loadingAccounts}
                      onClick={() => handleRegenerateHubKey(acc.user_id)}
                    >
                      {regenBusyId === acc.user_id ? "재발급 중..." : "재발급"}
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => handleDelete(acc.user_id)}
                      disabled={deletingId === acc.user_id || loadingAccounts}
                    >
                      <DeleteIcon
                        sx={{
                          color: deletingId === acc.user_id ? "#bbb" : "#f44336",
                        }}
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

            {!loadingAccounts && accounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ color: "#888", textAlign: "center", py: 4 }}>
                  표시할 계정이 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 계정 추가 다이얼로그 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>계정 추가</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="User ID"
            fullWidth
            variant="standard"
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
            placeholder="예) ghlee1"
            disabled={isCreating}
            sx={{ mb: 2 }}
          />
          
          {/* [추가됨] 조직 선택 Select */}
          <FormControl fullWidth variant="standard" margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="org-select-label">소속 (Organization)</InputLabel>
            <Select
              labelId="org-select-label"
              value={newOrgId}
              onChange={(e) => setNewOrgId(e.target.value)}
              label="소속 (Organization)"
              disabled={isCreating}
            >
              {orgs.length > 0 ? (
                orgs.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name} ({org.id})
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="KR">KR (기본)</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Tier 선택 Select */}
          <FormControl fullWidth variant="standard" margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="tier-select-label">권한 등급 (Tier)</InputLabel>
            <Select
              labelId="tier-select-label"
              value={newUserTier}
              onChange={(e) => setNewUserTier(Number(e.target.value))}
              label="권한 등급"
              disabled={isCreating}
            >
              <MenuItem value={1}>MCP Architect (생산자 - Tier 1)</MenuItem>
              <MenuItem value={2}>Agent Builder (사용자 - Tier 2)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Password"
            fullWidth
            variant="standard"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="8자리 이상"
            error={!!passwordError}
            helperText={passwordError || "비밀번호는 8자리 이상이어야 합니다."}
            disabled={isCreating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={isCreating}>
            취소
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newUserId.trim() || newPassword.length < 8 || isCreating}
          >
            {isCreating ? "생성 중..." : "생성"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserAccessManager;